import './AsyncSearch.css';
import './../input.scss';
import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { SelectableList } from '../../SelectableList/SelectableList';
import { getProductName, Product } from '../../../model/product';
import { useForm } from '../Form';
import { Event, getEventName } from '../../../model/event';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { fullUserName, User } from '../../../model/user';
import { Shortcut } from '../../Shortcut/Shortcut';
import {
  searchCollection,
  searchElementById,
  searchElementsByIds,
  OptionType,
  useRemoteSearch,
  Collection,
} from '../../../model/data';
import { handleError } from '../../../model/notification';
import { t } from '../../../locales/translate';
import { useErrorHandler } from '../../../util/util';
import { getVenueName, Venue } from '../../../model/venue';
import { Tooltip } from '../../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { TagsGroup } from '../../Tag/TagsGroup';
import { Spinner } from '../../Spinner/Spinner';

interface Props {
  name: string;
  label?: string;
  actions?: React.ReactNode | React.ReactNodeArray;
  searchIn: OptionType[];
  required?: boolean;
  position?: PopperJS.Placement;
  maxResults?: number;
  minQueryLength?: number;
  description?: string;
  icon?: IconName;
  isLoading?: boolean;
  errorOverride?: string;
  onSelect?: (item: Collection | Collection[] | undefined) => void;
  disabled?: boolean;
}

export function AsyncSearch({
  name,
  label,
  actions,
  searchIn,
  required,
  minQueryLength = 2,
  maxResults = 10,
  icon,
  errorOverride,
  isLoading: propLoading,
  description,
  onSelect,
  position = 'bottom-start',
  disabled,
}: Props): JSX.Element {
  const [rawValue, setRawValue, , reportValidity] = useForm<string | string[]>(name);
  const multiSelect = Array.isArray(rawValue);
  const [selectedItems, setSelectedItems] = useState<Collection[]>([]);
  const [pendingValue, setPendingValue] = useState(multiSelect ? ' ' : '');
  const [loadingInitialValue, setLoadingInitialValue] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [active, setActive] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      reportValidity(true);
    };
  }, []);

  async function getCurrentValue(): Promise<void> {
    try {
      setLoadingInitialValue(true);
      if (multiSelect) {
        const options = await searchElementsByIds(rawValue as string[], searchIn);
        setSelectedItems(options || []);
      } else {
        const option = await searchElementById(rawValue as string, searchIn);
        setDisplayValue(option ? getOptionLabel(option) || '' : '');
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingInitialValue(false);
    }
  }

  useEffect(() => {
    if (rawValue) {
      getCurrentValue();
    }
  }, []); // only when the component loads will the results be empty

  const value = multiSelect ? rawValue || [] : rawValue || '';

  const hasValue = (multiSelect ? value.length > 0 : value) || pendingValue.trim() || displayValue.trim();

  if (required) {
    reportValidity(!!hasValue);
  }

  const [search, searchLoading, searchError] = useRemoteSearch(
    pendingValue,
    async (term) => {
      return searchCollection({ term, types: searchIn });
    },
    minQueryLength
  );

  useErrorHandler(searchError);

  function renderOptions(): (string | undefined | null)[] {
    if (search) {
      return search.results.map((item) => getOptionLabel(item));
    }
    return [];
  }

  function removeTagAtIndex(index: number): void {
    const newSelected = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newSelected);
    setRawValue(newSelected.map((item) => item.id));
    if (onSelect) {
      onSelect(selectedItems);
    }
  }

  function removeLastTag(): void {
    if (!searchLoading) {
      const newSelected = [...selectedItems];
      newSelected.pop();
      setSelectedItems(newSelected);
      setRawValue(newSelected.map((item) => item.id));
      if (onSelect) {
        onSelect(selectedItems);
      }
    }
  }

  // Event Handlers
  async function handleOnChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    setPendingValue(e.currentTarget.value);
    setDisplayValue('');
  }

  function handleOnSelect(index: number): void {
    if (!searchLoading && search) {
      const option = search.results[index];
      if (option) {
        if (multiSelect) {
          if (!(rawValue as string[]).includes(option.id)) {
            const newSelectedItems = [...selectedItems, option];
            setSelectedItems(newSelectedItems);
            const newValue = [...(rawValue as string[])];
            setRawValue([...newValue, option.id]);
            setPendingValue(' ');
          }
        } else {
          setRawValue(option.id);
          setDisplayValue(getOptionLabel(search.results[index]) || pendingValue);
          setPendingValue('');
        }
      }
      if (onSelect) {
        onSelect(multiSelect ? selectedItems : search.results[index]);
      }
      setActive(false);
    }
  }

  function handleOnClear(): void {
    setRawValue('');
    setPendingValue('');
    setDisplayValue('');
    setActive(false);

    if (onSelect) {
      onSelect(undefined);
    }
  }

  const valueIsReady = multiSelect
    ? rawValue.length === selectedItems.length
    : !rawValue || !!pendingValue || !!displayValue;
  const foregroundLoading = !!propLoading || loadingInitialValue;
  const backgroundLoading = foregroundLoading || searchLoading || !valueIsReady;

  function renderInput(): JSX.Element {
    return (
      <div className="async-search__input-container">
        <input
          className="input__input"
          type="text"
          value={displayValue || pendingValue}
          id={name}
          autoComplete="off"
          onChange={handleOnChange}
          disabled={disabled || loadingInitialValue}
        />
        <Icon
          className={classnames('async-search__loading', {
            'async-search__loading--active': backgroundLoading,
          })}
          name="search--sm"
          loading={backgroundLoading}
        />
      </div>
    );
  }

  return (
    <div className="input async-search" ref={parentRef}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': hasValue || active,
        })}
      >
        <Tooltip
          style={{ width: parentRef.current?.clientWidth }}
          open={!disabled && active}
          onChange={(open) => {
            if (!disabled && !backgroundLoading) {
              setActive(open);
            }
          }}
          rounded={false}
          gap="none"
          arrow={false}
          position={position}
          mode="click"
          reference={
            <div className="input__input-wrapper">
              {label && (
                <label className="input__label" htmlFor={name}>
                  {label}
                  {!required && ` (${t.general.optional})`}
                </label>
              )}
              {multiSelect ? (
                <TagsGroup
                  tags={selectedItems.map((item) => ({
                    id: item.id,
                    label: getOptionLabel(item),
                  }))}
                  input={renderInput()}
                  actions={<Shortcut keys="Backspace" onInvoke={removeLastTag} overrideInput={active} />}
                  onRemove={removeTagAtIndex}
                  onClear={handleOnClear}
                  expandHiddenTags="top-end"
                  loading={foregroundLoading}
                />
              ) : (
                <>
                  {foregroundLoading ? <Spinner color="var(--color-text-label)" scale=".84" /> : renderInput()}
                  {!disabled && (pendingValue || displayValue) && (
                    <Icon className="async-search__clear" name="remove" onClick={handleOnClear}></Icon>
                  )}
                </>
              )}
              {actions && <div className="input__actions">{actions}</div>}
            </div>
          }
        >
          <SelectableList
            className="async-search__select-menu-items"
            onSelect={handleOnSelect}
            unSelectableFooter={
              search && search.total > maxResults ? (
                <span className="async-search__more-results">{t.errors.moreResults(search.total)}</span>
              ) : undefined
            }
            displayEmpty={!!search}
            options={renderOptions()}
          />
        </Tooltip>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          {errorOverride && <div className="input__error-message">{errorOverride}</div>}
        </div>
      </div>
    </div>
  );
}

function getOptionLabel(
  option: Collection & {
    itemType?: OptionType;
    sku?: string;
    email?: string;
    name?: string | null;
  }
): string | undefined | null {
  switch (option.itemType) {
    case 'product':
      return `${option.sku} - ${getProductName(option as Product)}`;
    case 'event':
      return getEventName(option as Partial<Event>, {
        sku: true,
        venueName: true,
        city: true,
        happeningFrom: true,
      });
    case 'user':
      return [fullUserName(option as User), option.email].filter(Boolean).join('    ');
    case 'venue':
      return getVenueName(option as Venue, { address: true });
    case 'venue_with_single_general_admission':
      return getVenueName(option as Venue, { address: true });
    default:
      return option.name;
  }
}
